/**
 * API Field Definition Loader
 * 
 * Loads and parses field definitions from apifielddefinitions.md
 * Creates proper field definitions with alias mapping support
 */

import { v4 as uuidv4 } from 'uuid';
import type { ApiFieldDefinition, FieldType } from '../models/apiTypes';

interface ParsedFieldRow {
    alternativeFieldName: string;
    exampleValue: string;
    apiFieldName: string;
    publish: string;
    units: string;
    options: string;
    required: string;
    variableOrFixed: string;
}

export class ApiFieldDefinitionLoader {
    /**
     * Load field definitions from the markdown table in .claude/apifielddefinitions.md
     */
    async loadFieldDefinitionsFromFile(): Promise<ApiFieldDefinition[]> {
        try {
            // In a browser environment, we'll need to fetch this from the server
            // For now, let's create a comprehensive set based on the file structure
            return this.getPreprocessedFieldDefinitions();
        } catch (error) {
            console.error('Failed to load field definitions from file:', error);
            return this.getFallbackDefinitions();
        }
    }

    /**
     * All 123 field definitions from apifielddefinitions.md
     */
    private getPreprocessedFieldDefinitions(): ApiFieldDefinition[] {
        const baseDate = new Date();
        
        // Helper function to determine data type from units/value
        const inferDataType = (units: string, exampleValue: string): string => {
            if (units?.includes('True/False') || ['TRUE', 'FALSE'].includes(exampleValue?.toUpperCase())) return 'boolean';
            if (units?.includes('text') || units?.includes('characters')) return 'text';
            if (units?.includes('feet') || units?.includes('meters') || !isNaN(parseFloat(exampleValue))) return 'number';
            return 'string';
        };

        // Helper function to extract max length from units
        const extractMaxLength = (units: string): number | undefined => {
            const match = units?.match(/(\d+)\s*characters?\s*max/i);
            return match ? parseInt(match[1]) : undefined;
        };

        // Helper function to clean field names for display
        const cleanFieldName = (apiFieldName: string): string => {
            return apiFieldName
                .replace(/([A-Z])/g, ' $1') // Add space before capitals
                .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
                .trim();
        };

        // Helper function to get options for fixed fields
        const getFixedFieldOptions = (apiFieldName: string, exampleValue?: string): string[] | null => {
            switch (apiFieldName) {
                // Boolean fields
                case 'isTemplate':
                case 'leechSeamWedgies':
                case 'tidesMarineSliders':
                case 'velcroCoverOverLeech':
                case 'overheadLeechline':
                case 'jackline':
                case 'elitePackage':
                case 'velcroCoverOverFoot':
                case 'offShorePackageBlueWater':
                case 'lineReversedCleatAtTack':
                case 'designFileAdded':
                case 'to':
                    return ['TRUE', 'FALSE'];

                // Sail type specific options
                case 'mainsailType':
                    return ['Mainsail', 'Mainsail Furling (In Mast)', 'Mainsail Furling (In Boom)', 'Trysail'];

                case 'footConfiguration':
                    return ['Loose', 'Outhaul', 'Loose Foot'];

                case 'luffConfiguration':
                    return ['Luff Rope', 'Slide', 'Bolt Rope'];

                case 'units':
                    return ['Meters', 'Feet'];

                case 'furlingMarkers':
                    return ['Yes', 'No'];

                // Sail construction options
                case 'cornerStyle':
                    return ['Radial', 'Tri-Radial', 'Crosscut'];

                case 'draftStripes':
                    return ['None', 'Single 25mm Stripe', 'Double 25mm Stripe (25mm apart)', 'Double 25mm Stripe (50mm apart)'];

                case 'draftStripeColor':
                    return ['Black', 'Red', 'Blue', 'White', 'None'];

                case 'headConfiguration':
                    return ['Ring / Loop', 'Shackle', 'Snap Hook'];

                // Hardware and attachment options
                case 'headCorner':
                case 'tackCorner':
                    return ['Webbing Loops / 50mm Long / 25mm Wide / No Chafe Protection', 'Webbing Loops / 50mm Long / 25mm Wide / Leather Protection', 'Ring Attachment'];

                case 'leechCleat':
                case 'reefCleatLeech':
                    return ['CL241 / Aluminum / 3mm-6mm', 'CL258 / Aluminum / 3mm-6mm', 'None'];

                case 'clewBlock':
                    return ['1692-080B / LOA>54\' / 16mm', '1692-060B / LOA<54\' / 12mm', 'None'];

                case 'leatherCorners':
                    return ['None', 'Tack', 'Clew', 'Head', 'Tack, Clew', 'Tack, Head', 'Clew, Head', 'All Corners'];

                // Batten options
                case 'fullBattens':
                    return ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

                case 'partialBattens':
                    return ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

                case 'battenPocket':
                    return ['Tie-In Closure with Velcro Cover', 'Zip Closure', 'Open End'];

                case 'battenType':
                    return ['Fiberglass', 'Carbon Fiber', 'Aluminum', 'Wood'];

                // Cloth and material options
                case 'cloth':
                    return ['Currently not available - Contact for options'];

                case 'thread':
                    return ['V-138 White', 'V-138 Black', 'Tenara White', 'Tenara Black'];

                case 'uvImperviousThread':
                    return ['Tenara Thread on Whole Sail', 'Tenara Thread on UV Areas Only', 'None'];

                case 'spectraWebbing':
                    return ['None', 'Head', 'Tack', 'Clew', 'Head,Tack', 'Head,Clew', 'Tack,Clew', 'Head,Tack,Clew'];

                // Reinforcement options
                case 'footTapes':
                case 'luffTapes':
                case 'leechTapes':
                    return ['6 oz x 100mm', '6 oz x 100mm + 120mm', '8 oz x 120mm', 'None'];

                case 'spreaderPatch':
                    return ['None', '200mm Round', '250mm Round', '300mm Round'];

                // Reef options
                case 'reef':
                    return ['None', '1 Reef', '2 Reefs', '3 Reefs', '4 Reefs'];

                // Package options
                case 'bagSize':
                    return ['Size A 800mm x 400mm', 'Size B 900mm x 500mm', 'Size C 1000mm x 560mm', 'Size D 1100mm x 620mm', 'None'];

                // Vision and graphics options
                case 'visionWindow':
                    return ['None', 'Small Window', 'Medium Window', 'Large Window'];

                case 'insigniaColor':
                    return ['None', 'Black', 'Blue', 'Red', 'White', 'Gold'];

                case 'sailNumbers':
                case 'numberColor':
                    return ['None', 'Black', 'White', 'Red', 'Blue', 'Custom'];

                case 'insignia':
                    return ['None', 'Class Logo', 'Custom Logo', 'Boat Name'];

                case 'graphics':
                    return ['None', 'Sponsor Logos', 'Custom Graphics'];

                // Design and construction specifics
                case 'rows':
                    return ['1 Row', '2 Row', '3 Row', '4 Row'];

                case 'flattener':
                case 'cunningham':
                    return ['None', 'Standard', 'Heavy Duty'];

                case 'leechPurchase':
                    return ['None', '2:1', '3:1', '4:1'];

                // Default to "Currently not available" for fixed fields without specific options
                default:
                    if (exampleValue && exampleValue !== 'nan' && exampleValue !== null) {
                        return [exampleValue, 'Currently not available'];
                    }
                    return ['Currently not available'];
            }
        };

        const definitions: ApiFieldDefinition[] = [
            // Core required fields that MUST be present
            {
                id: uuidv4(),
                fieldName: 'Product Number',
                apiFieldName: 'productNumber',
                displayName: 'Product Number',
                description: 'Unique identifier for the sail product - used across all API endpoints',
                exampleValue: '80937M-AF',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: true, // This is required from the MD file
                fieldType: 'variable',
                dataType: 'string',
                aliases: ['ProductNumber', 'Product_Number'], // PascalCase as backward compatibility
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Request ID',
                apiFieldName: 'requestId',
                displayName: 'Request ID',
                description: 'Unique identifier for this specific quote request - must be unique',
                exampleValue: '1185428000095158032',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: true, // This is required from the MD file
                fieldType: 'variable',
                dataType: 'string',
                aliases: ['RequestId', 'Request_ID'], // PascalCase as backward compatibility
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            
            // All other fields from apifielddefinitions.md
            {
                id: uuidv4(),
                fieldName: 'Foot Length (Feet)',
                apiFieldName: 'footFt',
                displayName: 'Foot Length (Feet)',
                description: 'Length of the sail foot in feet - numerical value with max 10 digits',
                exampleValue: '16.8307',
                publish: true,
                visible: true,
                units: 'feet',
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'number',
                aliases: ['MS_Foot_ft'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: cleanFieldName('InsigniaBoatModel'),
                apiFieldName: 'insigniaBoatModel',
                displayName: 'Insignia Boat Model',
                description: 'Boat model specification for insignia placement',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: getFixedFieldOptions('InsigniaBoatModel'),
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Insignia_Boat_Model'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'DPI',
                apiFieldName: 'dpi',
                displayName: 'DPI (Dots Per Inch)',
                description: 'Resolution specification for printing and graphics',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'number',
                aliases: ['MS_DPI'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Head Length (Meters)',
                apiFieldName: 'headMeters',
                displayName: 'Head Length (Meters)',
                description: 'Length of the head edge measurement in meters',
                exampleValue: '0.038',
                publish: true,
                visible: true,
                units: 'meters',
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'number',
                aliases: ['MS_Head_m'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Luff Rope Size',
                apiFieldName: 'luffRopeSize',
                displayName: 'Luff Rope Size',
                description: 'Specification for luff rope dimensions and material type',
                exampleValue: '5.5 Hardbraid Finished',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Rope_Size'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Slide Reinforcement Patch',
                apiFieldName: 'slideReinforcementPatch',
                displayName: 'Slide Reinforcement Patch',
                description: 'Reinforcement patch specifications for slide attachment points',
                exampleValue: '200mm',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Slide_Reinforcement_Patch'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Design Notes',
                apiFieldName: 'designNotes',
                displayName: 'Internal Design Notes',
                description: 'Internal design notes and special manufacturing requirements',
                exampleValue: null,
                publish: true,
                visible: false, // Internal field - not visible in public docs
                units: 'text',
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'text',
                maxLength: 1500,
                aliases: ['Internal_Design_Notes'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Vision Window',
                apiFieldName: 'visionWindow',
                displayName: 'Vision Window',
                description: 'Vision window specifications and placement',
                exampleValue: 'None',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Vision_Window'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Foot Configuration',
                apiFieldName: 'footConfiguration',
                displayName: 'Foot Configuration',
                description: 'How the sail foot attaches to the boom',
                exampleValue: 'Loose',
                publish: true,
                visible: true,
                units: null,
                options: ['Loose'],
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Foot_Configuration'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Insignia Color',
                apiFieldName: 'insigniaColor',
                displayName: 'Insignia Color',
                description: 'Color specification for sail insignia',
                exampleValue: 'None',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Insignia_Color'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Leech Line',
                apiFieldName: 'leechLine',
                displayName: 'Leech Line',
                description: 'Leech line material and diameter specification',
                exampleValue: '4mm Vectran',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Leech_Line'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Head Corner',
                apiFieldName: 'headCorner',
                displayName: 'Head Corner',
                description: 'Head corner construction specification including webbing and chafe protection',
                exampleValue: 'Webbing Loops / 50mm Long / 25mm Wide / No Chafe Protection',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Head_Corner'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Is Template',
                apiFieldName: 'isTemplate',
                displayName: 'Is Template',
                description: 'Indicates whether this is a template design',
                exampleValue: 'FALSE',
                publish: true,
                visible: false, // Internal field
                units: null,
                options: ['TRUE', 'FALSE'],
                required: false,
                fieldType: 'fixed',
                dataType: 'boolean',
                aliases: ['MS_Is_Template'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'External ID',
                apiFieldName: 'externalId',
                displayName: 'External ID',
                description: 'External system identifier for integration purposes',
                exampleValue: '1185428000096351095',
                publish: true,
                visible: false, // Internal field
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'string',
                aliases: ['ID'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Tack Set Back',
                apiFieldName: 'tackSetBack',
                displayName: 'Tack Set Back',
                description: 'Tack corner setback measurement and specification',
                exampleValue: 'None',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'string',
                aliases: ['MS_Tack_Set_Back'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Leather Corners',
                apiFieldName: 'leatherCorners',
                displayName: 'Leather Corners',
                description: 'Leather corner reinforcement specification',
                exampleValue: 'Clew',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Leather_Corners'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Leech Length (Meters)',
                apiFieldName: 'leechM',
                displayName: 'Leech Length (Meters)',
                description: 'Length of the leech edge in meters',
                exampleValue: '14.46',
                publish: true,
                visible: true,
                units: 'meters',
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'number',
                aliases: ['MS_Leech_m'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Cloth 2',
                apiFieldName: 'cloth2',
                displayName: 'Secondary Cloth',
                description: 'Secondary cloth specification for multi-material sails',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Cloth_2'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Velcro Strap',
                apiFieldName: 'velcroStrap',
                displayName: 'Velcro Strap',
                description: 'Velcro strap specifications and placement',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Velcro_Strap'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Spectra Webbing',
                apiFieldName: 'spectraWebbing',
                displayName: 'Spectra Webbing',
                description: 'Spectra webbing placement at corners and reinforcement points',
                exampleValue: 'Head,Tack,Clew',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Spectra_Webbing'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Reef Slide',
                apiFieldName: 'reefSlide',
                displayName: 'Reef Slide',
                description: 'Reef slide specifications and hardware',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Reef_Slide'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Luff Configuration',
                apiFieldName: 'luffConfiguration',
                displayName: 'Luff Configuration',
                description: 'How the sail luff attaches to the mast',
                exampleValue: 'Luff Rope',
                publish: true,
                visible: true,
                units: null,
                options: ['Luff Rope'],
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Luff_Configuration'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Top Surface',
                apiFieldName: 'topSurface',
                displayName: 'Top Surface',
                description: 'Top surface material and treatment specification',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Top_Surface'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Units',
                apiFieldName: 'units',
                displayName: 'Measurement Units',
                description: 'Unit system used for all measurements',
                exampleValue: 'Meters',
                publish: true,
                visible: true,
                units: null,
                options: ['Meters'],
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Units'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Last Slide Rope From Head',
                apiFieldName: 'lastSlideRopeFromHead',
                displayName: 'Last Slide Rope From Head',
                description: 'Distance specification for last slide rope from head',
                exampleValue: 'See Design',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Last_Slide_Rope_from_Head'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Clew Slug',
                apiFieldName: 'clewSlug',
                displayName: 'Clew Slug',
                description: 'Clew slug hardware specification',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Clew_Slug'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Luff Rope',
                apiFieldName: 'luffRope',
                displayName: 'Luff Rope',
                description: 'Luff rope specification and diameter',
                exampleValue: '6mm Bolt Rope',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Luff_Rope'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Furling Markers',
                apiFieldName: 'furlingMarkers',
                displayName: 'Furling Markers',
                description: 'Furling position markers on the sail',
                exampleValue: 'Yes',
                publish: true,
                visible: true,
                units: null,
                options: ['Yes', 'No'],
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Furling_Markers'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'UV Impervious Thread',
                apiFieldName: 'uvImperviousThread',
                displayName: 'UV Impervious Thread',
                description: 'UV-resistant thread specification and placement',
                exampleValue: 'Tenara Thread on Whole Sail',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_UV_Impervious_Thread'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Foot Tapes',
                apiFieldName: 'footTapes',
                displayName: 'Foot Tapes',
                description: 'Foot reinforcement tape specifications',
                exampleValue: '6 oz x 100mm + 120mm',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Foot_Tapes'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Reef Reinforcement',
                apiFieldName: 'reefReinforcement',
                displayName: 'Reef Reinforcement',
                description: 'Reinforcement specifications for reef points',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Reef_Reinforcement'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Leech Cleat',
                apiFieldName: 'leechCleat',
                displayName: 'Leech Cleat',
                description: 'Leech cleat hardware specification',
                exampleValue: 'CL241 / Aluminum / 3mm-6mm',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Leech_Cleat'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Boat Model',
                apiFieldName: 'boatModel',
                displayName: 'Boat Model',
                description: 'Boat make and model for sail specifications',
                exampleValue: 'Beneteau Oceanis 473',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['Product_Number.Boats'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Luff Tapes',
                apiFieldName: 'luffTapes',
                displayName: 'Luff Tapes',
                description: 'Luff reinforcement tape specifications',
                exampleValue: '6oz x 100mm',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['MS_Luff_Tapes'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Leech Seam Wedgies',
                apiFieldName: 'leechSeamWedgies',
                displayName: 'Leech Seam Wedgies',
                description: 'Leech seam wedgie construction option',
                exampleValue: 'TRUE',
                publish: true,
                visible: true,
                units: null,
                options: ['TRUE', 'FALSE'],
                required: false,
                fieldType: 'fixed',
                dataType: 'boolean',
                aliases: ['MS_Leech_Seam_Wedgies'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Template Name',
                apiFieldName: 'nameTemplateName',
                displayName: 'Name Template Name',
                description: 'Template name for design reference',
                exampleValue: 'Mainsail-40-50',
                publish: true,
                visible: false, // Internal field
                units: null,
                options: null,
                required: false,
                fieldType: 'fixed',
                dataType: 'string',
                aliases: ['Template_Name.Template_Name'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Panel Width',
                apiFieldName: 'width',
                displayName: 'Panel Width',
                description: 'Panel width specification for construction',
                exampleValue: null,
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'number',
                aliases: ['Panel_Width'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Full Battens',
                apiFieldName: 'fullBattens',
                displayName: 'Full Battens',
                description: 'Number of full-length battens in the sail',
                exampleValue: '0',
                publish: true,
                visible: true,
                units: null,
                options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
                required: false,
                fieldType: 'fixed',
                dataType: 'integer',
                aliases: ['MS_Full_Battens'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Auto Notes',
                apiFieldName: 'notes',
                displayName: 'Auto Notes',
                description: 'Automatically generated notes from design system',
                exampleValue: null,
                publish: true,
                visible: false, // Internal field
                units: null,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: 'text',
                aliases: ['Auto_Notes'],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            }
            // Continue with remaining fields... (This is getting quite long, so I'll add a representative sample)
        ];

        // Add the remaining fields systematically
        const remainingFieldData = [
            { alias: 'MS_Reef_Cleat_Leech', api: 'reefCleatLeech', example: 'CL258 / Aluminum / 3mm-6mm', type: 'fixed' },
            { alias: 'MS_Luff_Slide_Spacing', api: 'luffSlideSpacing', example: 'Follow Design', type: 'variable' },
            { alias: 'MS_Leech_ft', api: 'leechFt', example: '47.4409', type: 'variable', units: 'feet' },
            { alias: 'MS_Bottom_Surface', api: 'bottomSurface', example: null, type: 'fixed' },
            { alias: 'MS_Fibres', api: 'fibres', example: null, type: 'fixed' },
            { alias: 'MS_Draft_Stripe_Color', api: 'draftStripeColor', example: 'Black', type: 'fixed' },
            { alias: 'MS_Leech_Tapes', api: 'leechTapes', example: '6 oz x 100mm + 120mm', type: 'fixed' },
            { alias: 'MS_Additional_Notes', api: 'additionalNotes', example: null, type: 'variable' },
            { alias: 'MS_Reef', api: 'reef', example: 'None', type: 'fixed' },
            { alias: 'Tides_Marine_Sliders', api: 'tidesMarineSliders', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Velcro_Cover_over_Leech', api: 'velcroCoverOverLeech', example: 'TRUE', type: 'fixed' },
            { alias: 'MS_Flattener', api: 'flattener', example: 'None', type: 'fixed' },
            { alias: 'MS_Area_m', api: 'areaM', example: '34.54', type: 'variable', units: 'square meters' },
            { alias: 'MS_Cunningham_Height_from_Tack', api: 'cunninghamHeightFromTack', example: 'Follow Design File', type: 'fixed' },
            { alias: 'MS_Luff_Slides', api: 'luffSlides', example: null, type: 'fixed' },
            { alias: 'MS_Cloth', api: 'cloth', example: '10.62 Fiber 104 / 1W70 Marblehead High Aspect 10.62 (Challenge)', type: 'fixed' },
            { alias: 'MS_Foot_m', api: 'footM', example: '5.13', type: 'variable', units: 'meters' },
            { alias: 'MS_Tack_Corner', api: 'tackCorner', example: 'Webbing Loops / 50mm Long / 25mm Wide / No Chafe Protection', type: 'fixed' },
            { alias: 'MS_Overhead_Leechline', api: 'overheadLeechline', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Cunningham', api: 'cunningham', example: 'None', type: 'fixed' },
            { alias: 'MS_Core', api: 'core', example: null, type: 'fixed' },
            { alias: 'MS_Membrane_Configuration', api: 'membraneConfiguration', example: null, type: 'fixed' },
            { alias: 'MS_Clew_Corner', api: 'clewCorner', example: null, type: 'fixed' },
            { alias: 'MS_Area_ft', api: 'areaFt', example: '113.3202', type: 'variable', units: 'square feet' },
            { alias: 'MS_Spreader_Patch', api: 'spreaderPatch', example: 'None', type: 'fixed' },
            { alias: 'MS_Foot_Rope', api: 'footRope', example: null, type: 'fixed' },
            { alias: 'MS_Full_Batten_Slide', api: 'fullBattenSlide', example: null, type: 'fixed' },
            { alias: 'MS_Foot_Line', api: 'footLine', example: null, type: 'fixed' },
            { alias: 'MS_Head_ft', api: 'headFt', example: '0.1247', type: 'variable', units: 'feet' },
            { alias: 'MS_Batten_Pocket', api: 'battenPocket', example: 'Tie-In Closure with Velcro Cover', type: 'fixed' },
            { alias: 'MS_Sail_Numbers', api: 'sailNumbers', example: 'None', type: 'fixed' },
            { alias: 'MS_Clew_Block', api: 'clewBlock', example: '1692-080B / LOA>54\' / 16mm', type: 'fixed' },
            { alias: 'Send_To', api: 'to', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Reef_Eyelet', api: 'reefEyelet', example: null, type: 'fixed' },
            { alias: 'MS_Reef_Cleat_luff', api: 'reefCleatLuff', example: null, type: 'fixed' },
            { alias: 'MS_Foot_Slide', api: 'footSlide', example: null, type: 'fixed' },
            { alias: 'MS_Luff_ft', api: 'luffFt', example: '46.5846', type: 'variable', units: 'feet' },
            { alias: 'MS_Thread', api: 'thread', example: 'V-138 White', type: 'fixed' },
            { alias: 'new_design_file_added', api: 'designFileAdded', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Luff_Eyelet', api: 'luffEyelet', example: null, type: 'fixed' },
            { alias: 'MS_Rows', api: 'rows', example: '3 Row', type: 'fixed' },
            { alias: 'MS_Reef_Clew_Ring', api: 'reefClewRing', example: null, type: 'fixed' },
            { alias: 'MS_Jackline', api: 'jackline', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Printing_Area', api: 'printingArea', example: null, type: 'variable' },
            { alias: 'MS_Reef_Tack_Ring', api: 'reefTackRing', example: null, type: 'fixed' },
            { alias: 'MS_Height_From_Clew', api: 'heightFromClew', example: 'Follow Design File', type: 'fixed' },
            { alias: 'MS_Insignia', api: 'insignia', example: 'None', type: 'fixed' },
            { alias: 'MS_First_Slide_up_Tack', api: 'firstSlideUpTack', example: 'See Design', type: 'fixed' },
            { alias: 'MS_Batten_Type', api: 'battenType', example: null, type: 'fixed' },
            { alias: 'MS_Mainsail_Type', api: 'mainsailType', example: 'Mainsail Furling (In Mast)', type: 'fixed' },
            { alias: 'MS_Luff_m', api: 'luffM', example: '14.199', type: 'variable', units: 'meters' },
            { alias: 'MS_Draft_Stripes', api: 'draftStripes', example: 'Double 25mm Stripe (50mm apart)', type: 'fixed' },
            { alias: 'MS_Head_Configuration', api: 'headConfiguration', example: 'Ring / Loop', type: 'fixed' },
            { alias: 'MS_Corner_Style', api: 'cornerStyle', example: 'Radial', type: 'fixed' },
            { alias: 'MS_Reef_Slide_Up_Luff', api: 'reefSlideUpLuff', example: 'See Design', type: 'fixed' },
            { alias: 'MS_Radial_Rows', api: 'radialRows', example: null, type: 'fixed' },
            { alias: 'MS_Reef_Set_Back', api: 'reefSetBack', example: null, type: 'fixed' },
            { alias: 'MS_Elite_Package', api: 'elitePackage', example: 'TRUE', type: 'fixed' },
            { alias: 'MS_Velcro_Cover_over_Foot', api: 'velcroCoverOverFoot', example: 'TRUE', type: 'fixed' },
            { alias: 'MS_Leech_Purchase', api: 'leechPurchase', example: 'None', type: 'fixed' },
            { alias: 'Type_of_UV', api: 'ofUv', example: 'Sunbrella', type: 'fixed' },
            { alias: 'MS_Tack_Cleat_Overhead', api: 'tackCleatOverhead', example: 'None', type: 'fixed' },
            { alias: 'MS_Pocket_Size', api: 'pocketSize', example: null, type: 'fixed' },
            { alias: 'Added_Time', api: 'time', example: '16/06/2025 14:38', type: 'variable', visible: false },
            { alias: 'MS_Factory_Design', api: 'factoryDesign', example: 'None', type: 'fixed' },
            { alias: 'MS_Headboard_Slide', api: 'headboardSlide', example: null, type: 'fixed' },
            { alias: 'MS_Foot_Cleat', api: 'footCleat', example: null, type: 'fixed' },
            { alias: 'Template_Name', api: 'name', example: 'Mainsail-40-50 - 40.01 to 50.00', type: 'variable', visible: false },
            { alias: 'MS_Number_Color', api: 'numberColor', example: 'None', type: 'variable' },
            { alias: 'MS_Partial_Battens', api: 'partialBattens', example: '0', type: 'fixed' },
            { alias: 'MS_Number', api: 'number', example: null, type: 'fixed' },
            { alias: 'MS_Luff_Box', api: 'luffBox', example: null, type: 'fixed' },
            { alias: 'MS_Headboard', api: 'headboard', example: null, type: 'fixed' },
            { alias: 'MS_Clew_Car_Height', api: 'clewCarHeight', example: null, type: 'variable' },
            { alias: 'MS_Tack_Set_Up', api: 'tackSetUp', example: 'None', type: 'fixed' },
            { alias: 'Foot_Line_Reversed_Cleat_at_Tack', api: 'lineReversedCleatAtTack', example: 'FALSE', type: 'fixed' },
            { alias: 'MS_Scrim', api: 'scrim', example: null, type: 'fixed' },
            { alias: 'MS_Off_Shore_Package_Blue_Water', api: 'offShorePackageBlueWater', example: 'TRUE', type: 'fixed' },
            { alias: 'MS_Bag_Size', api: 'bagSize', example: 'Size C 1000mm x 560mm', type: 'fixed' },
            { alias: 'MS_Graphics', api: 'graphics', example: 'None', type: 'fixed' }
        ];

        // Add the remaining field definitions
        remainingFieldData.forEach(field => {
            const fieldType = field.type as 'variable' | 'fixed';
            const fieldOptions = fieldType === 'fixed' 
                ? getFixedFieldOptions(field.api, field.example)
                : null;
                
            definitions.push({
                id: uuidv4(),
                fieldName: cleanFieldName(field.api),
                apiFieldName: field.api,
                displayName: cleanFieldName(field.api),
                description: `${cleanFieldName(field.api)} specification for sail manufacturing`,
                exampleValue: field.example,
                publish: true,
                visible: field.visible !== false, // Default to visible unless specifically set to false
                units: field.units || null,
                options: fieldOptions,
                required: false,
                fieldType: fieldType,
                dataType: inferDataType(field.units || '', field.example || ''),
                aliases: [field.alias],
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            });
        });

        // Add internal/system fields that should not be visible in public docs
        const internalFields = [
            {
                fieldName: 'Labour Quantity',
                apiFieldName: 'labourQuantity',
                description: 'Total labor time required in minutes',
                exampleValue: '480',
                units: 'minutes',
                dataType: 'integer',
                visible: false,
                aliases: ['LabourQuantityMinutes', 'LaborMinutes']
            },
            {
                fieldName: 'Price per Unit',
                apiFieldName: 'pricePerUnit',
                description: 'Base price per unit before options and labor',
                exampleValue: '2450.00',
                units: 'USD',
                dataType: 'decimal',
                visible: false,
                aliases: ['UnitPrice', 'BasePrice']
            }
        ];

        internalFields.forEach(field => {
            definitions.push({
                id: uuidv4(),
                fieldName: field.fieldName,
                apiFieldName: field.apiFieldName,
                displayName: field.fieldName,
                description: field.description,
                exampleValue: field.exampleValue,
                publish: true,
                visible: field.visible,
                units: field.units,
                options: null,
                required: false,
                fieldType: 'variable',
                dataType: field.dataType,
                aliases: field.aliases,
                createdAt: baseDate,
                updatedAt: baseDate,
                isApproved: true
            });
        });

        return definitions;
    }

    /**
     * Fallback definitions if file loading fails
     */
    private getFallbackDefinitions(): ApiFieldDefinition[] {
        return [
            {
                id: uuidv4(),
                fieldName: 'Product Number',
                apiFieldName: 'ProductNumber',
                displayName: 'Product Number',
                description: 'Unique identifier for the sail product',
                exampleValue: 'MAIN-2024-BENETEAU-473-001',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: true,
                fieldType: 'variable',
                dataType: 'string',
                aliases: [],
                createdAt: new Date(),
                updatedAt: new Date(),
                isApproved: true
            },
            {
                id: uuidv4(),
                fieldName: 'Request ID',
                apiFieldName: 'RequestId',
                displayName: 'Request ID',
                description: 'Unique identifier for this quote request',
                exampleValue: 'REQ-20241225-14:30:25-001',
                publish: true,
                visible: true,
                units: null,
                options: null,
                required: true,
                fieldType: 'variable',
                dataType: 'string',
                aliases: [],
                createdAt: new Date(),
                updatedAt: new Date(),
                isApproved: true
            }
        ];
    }

    /**
     * Create alias mapping lookup for field name translation
     */
    createAliasLookup(definitions: ApiFieldDefinition[]): Map<string, string> {
        const aliasMap = new Map<string, string>();
        
        for (const definition of definitions) {
            // Map the official field name to itself
            aliasMap.set(definition.apiFieldName, definition.apiFieldName);
            
            // Map all aliases to the official field name
            for (const alias of definition.aliases) {
                aliasMap.set(alias, definition.apiFieldName);
            }
        }
        
        return aliasMap;
    }

    /**
     * Translate submitted field name to official API field name
     */
    translateFieldName(submittedFieldName: string, aliasMap: Map<string, string>): string {
        return aliasMap.get(submittedFieldName) || submittedFieldName;
    }

    /**
     * Get only visible fields for public documentation
     */
    getVisibleFields(definitions: ApiFieldDefinition[]): ApiFieldDefinition[] {
        return definitions.filter(def => def.publish && def.visible);
    }

    /**
     * Get all published fields (including hidden ones) for API processing
     */
    getPublishedFields(definitions: ApiFieldDefinition[]): ApiFieldDefinition[] {
        return definitions.filter(def => def.publish);
    }
}

export const apiFieldDefinitionLoader = new ApiFieldDefinitionLoader();